import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, CalendarDays, Trash2, Archive, MailOpen, Inbox as InboxIcon, ExternalLink } from "lucide-react";
import { api } from "@/lib/auth";

const FILTERS = [
  { id: "new", label: "New" },
  { id: "read", label: "Read" },
  { id: "archived", label: "Archived" },
  { id: "all", label: "All" },
];

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("new");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/messages");
      setMessages(data);
    } catch {
      toast.error("Could not load messages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    const { data } = await api.patch(`/admin/messages/${id}`, { status });
    setMessages((ms) => ms.map((m) => (m.id === id ? data : m)));
  };

  const remove = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    setMessages((ms) => ms.filter((m) => m.id !== id));
    toast.success("Message deleted");
  };

  const visible = messages.filter((m) => filter === "all" || m.status === filter);
  const counts = Object.fromEntries(FILTERS.map((f) => [f.id, f.id === "all" ? messages.length : messages.filter((m) => m.status === f.id).length]));

  return (
    <section className="gold-frame rounded-2xl bg-obsidian-800 p-6 sm:p-8" data-testid="inbox-panel">
      <div className="flex flex-wrap items-center gap-2" data-testid="inbox-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`inbox-filter-${f.id}`}
            className={`rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              filter === f.id ? "bg-gold text-obsidian-900" : "border border-gold/20 text-ivory/60 hover:text-ivory"
            }`}
          >
            {f.label} <span className="ml-1 opacity-70">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/40">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="mt-10 flex flex-col items-center gap-3 py-10 text-center" data-testid="inbox-empty">
          <InboxIcon className="h-8 w-8 text-gold/50" />
          <p className="text-sm text-ivory/50">Nothing here. New enquiries from the contact form will appear instantly.</p>
        </div>
      ) : (
        <ul className="mt-8 space-y-4" data-testid="inbox-list">
          {visible.map((m) => (
            <li
              key={m.id}
              className={`rounded-xl border p-5 transition-colors ${
                m.status === "new" ? "border-gold/40 bg-obsidian-900" : "border-gold/10 bg-obsidian-900/60"
              }`}
              data-testid={`inbox-message-${m.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-xl text-ivory">{m.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/80">
                    {m.service || "General"} · {fmt(m.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {m.status !== "read" && (
                    <button onClick={() => setStatus(m.id, "read")} title="Mark read" data-testid={`inbox-mark-read-${m.id}`} className="rounded-full border border-gold/20 p-2 text-ivory/60 hover:text-gold">
                      <MailOpen className="h-4 w-4" />
                    </button>
                  )}
                  {m.status !== "archived" && (
                    <button onClick={() => setStatus(m.id, "archived")} title="Archive" data-testid={`inbox-archive-${m.id}`} className="rounded-full border border-gold/20 p-2 text-ivory/60 hover:text-gold">
                      <Archive className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => remove(m.id)} title="Delete" data-testid={`inbox-delete-${m.id}`} className="rounded-full border border-gold/20 p-2 text-ivory/60 hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-ivory/80">{m.message}</p>

              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 text-gold hover:text-gold-light">
                  <Mail className="h-4 w-4" /> {m.email}
                </a>
                {m.phone && (
                  <a href={`tel:${m.phone}`} className="inline-flex items-center gap-2 text-gold hover:text-gold-light">
                    <Phone className="h-4 w-4" /> {m.phone}
                  </a>
                )}
                {m.trip_date && (
                  <span className="inline-flex items-center gap-2 text-ivory/70">
                    <CalendarDays className="h-4 w-4 text-gold" /> {m.trip_date} {m.trip_time}
                  </span>
                )}
              </div>

              <div className="mt-4 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.18em]">
                <span className={`rounded-full px-3 py-1 ${m.emailed ? "bg-gold/15 text-gold" : "border border-ivory/10 text-ivory/40"}`}>
                  {m.emailed ? "Emailed to Gmail" : "Not emailed"}
                </span>
                {m.calendar_link ? (
                  <a href={m.calendar_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-3 py-1 text-gold">
                    Calendar event <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="rounded-full border border-ivory/10 px-3 py-1 text-ivory/40">No calendar event</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
